import type { ActionDoc } from "@shared/models/action"

/** How long a first attempt may sit unacknowledged before the poll re-sends it. */
const STUCK_AFTER_MS = 15000

/**
 * Whether the action poll should re-send this action.
 *
 * The second clause covers an attempt the channel resolved neither way — most reachably one
 * published while `channel` was undefined, which returns before the push and leaves the action at
 * "waiting" forever. It reads `sync.attemptCount`, the only counter the client writes (see
 * `_publishNewAction`); the `attributes.attemptCount` beside it is initialized to 0 and never
 * touched, so a predicate reading that one can never fire. Matching exactly one attempt is what
 * bounds this to a single extra try — the re-send makes the count two.
 *
 * `clientSentAt` is written by date-fns v1 `format`, i.e. an ISO string despite the model typing
 * it as a Date; `new Date` reads both.
 */
export function needsRetry(action: ActionDoc, now: Date): boolean {
  if (action.attributes.sync.status === "failed") return true
  if (action.attributes.sync.attemptCount !== 1) return false
  // Without this, `new Date(null)` reads as the epoch and every unsent action looks overdue.
  if (!action.attributes.timing.clientSentAt) return false

  const sentAt = new Date(action.attributes.timing.clientSentAt).valueOf()
  return now.valueOf() - sentAt > STUCK_AFTER_MS
}
